// components/EditionModale/HeaderEditorModal.tsx
'use client';

import { useEffect, useState } from 'react';
import {
    Button,
    Modal,
    Stack,
    Group,
    TextInput,
    ActionIcon,
    Title,
    Select,
    Switch,
    Textarea,
    Accordion,
} from '@mantine/core';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { Dropzone } from '../EditorText/Dropzone';
import { usePathname } from 'next/navigation';
import { i18n } from '@/i18n-config';
import { addDeploymentToast } from '@/src/helpers/add-deployment-tip';
import Cookies from 'js-cookie';
import { parseJwt } from '@/src/helpers/decode-jwt';

type LinkItem = {
    label: string;
    link: string;
    icon?: string;
    links?: LinkItem[];
};

type Props = {
    metaHeader?: LinkItem[];
    mainHeader: LinkItem[];
};

type AvailableDropdown = {
    value: string;
    label: string;
};

export default function HeaderEditorModal({ metaHeader, mainHeader }: Props) {
    const [opened, setOpened] = useState(false);
    const [meta, setMeta] = useState<LinkItem[]>(metaHeader ?? []);
    const [main, setMain] = useState<LinkItem[]>(mainHeader);
    const [slug, setSlug] = useState('');
    const [description, setDescription] = useState('');
    const [isDropdown, setIsDropdown] = useState(false);
    const [selectedMenuLink, setSelectedMenuLink] = useState<string | null>(null);
    const [createDropdown, setCreateDropdown] = useState(false);
    const [newDropdownLabel, setNewDropdownLabel] = useState('');
    const [loading, setLoading] = useState(false);
    const dictionary = useDictionary();
    const [logoUrl, setLogoUrl] = useState(dictionary.logoUrl);
    const pathname = usePathname();
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const locale = i18n.locales.find((loc) => pathname.startsWith(`/${loc}`)) || i18n.defaultLocale;

    useEffect(() => {
        const token = Cookies.get('authToken');
        if (token) {
            try {
                const decoded = parseJwt(token);
                if (decoded?.role === 'superAdmin') {
                    setIsSuperAdmin(true);
                }
            } catch (err) {
                Cookies.remove('authToken');
                window.location.reload();
                console.error('Invalid token', err);
            }
        }
    }, []);


    const pageOptions = Object.entries(dictionary.pages).map(([key, page]) => {
        // @ts-ignore - TO CHECK
        const link = page?.link || `/${key}`;
        // @ts-ignore - TO CHECK
        const label = page?.label || key;
        return {
            value: link,
            label: `${key} (${label})`,
        };
    });

    const availableDropdowns = dictionary.header.header
        .filter((item: any) => Array.isArray(item.links) && item.label)
        .map((item: any) => ({
            value: item.label.toLowerCase().replace(/\s+/g, '-'), // use label as fallback value
            label: item.label,
        } as AvailableDropdown))
        .filter((item: AvailableDropdown) => item.value); // make sure value exists

    const handleSave = async () => {
        const res = await fetch('/api/update-header', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                metaHeader: meta,
                header: main,
                logoUrl,
                locale,
            }),
        });

        if (res.ok) {
            const { hash } = await res.json();
            addDeploymentToast('Déploiement du header en cours. Optimisation des fichiers...', locale, hash);
            setOpened(false);
        } else {
            alert('Erreur lors de la sauvegarde');
        }
    };

    const handleCreatePage = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/generate-page', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    slug,
                    description,
                    targetMenuKey: isDropdown ? selectedMenuLink || slug : slug,
                    createDropdown,
                    newDropdownLabel: createDropdown ? newDropdownLabel : undefined,
                }),
            });

            const result = await res.json();
            if (res.ok) {
                addDeploymentToast('Déploiement du header en cours. Optimisation des fichiers...');
                setOpened(false);
            } else {
                alert(result.error || 'Erreur lors de la création de la page');
            }
        } catch (e) {
            alert('Erreur serveur');
        } finally {
            setLoading(false);
        }
    };

    const updateItem = (
        section: 'meta' | 'main',
        index: number,
        key: keyof LinkItem,
        value: string,
        subIndex?: number
    ) => {
        const state = structuredClone(section === 'meta' ? meta : main);

        if (typeof subIndex === 'number' && state[index].links) {
            // @ts-ignore - TO CHECK
            state[index].links![subIndex][key] = value;
        } else {
            // @ts-ignore - TO CHECK
            state[index][key] = value;
        }

        section === 'meta' ? setMeta(state) : setMain(state);
    };

    const addItem = (section: 'meta' | 'main') => {
        const newItem: LinkItem = { label: '', link: '' };
        section === 'meta' ? setMeta([...meta, newItem]) : setMain([...main, newItem]);
    };

    const removeItem = (section: 'meta' | 'main', index: number) => {
        section === 'meta'
            ? setMeta(meta.filter((_, i) => i !== index))
            : setMain(main.filter((_, i) => i !== index));
    };

    const addDropdownItem = (mainIndex: number) => {
        const updated = structuredClone(main);
        if (!updated[mainIndex].links) updated[mainIndex].links = [];
        updated[mainIndex].links!.push({ label: '', link: '' });
        setMain(updated);
    };

    const removeDropdownItem = (mainIndex: number, subIndex: number) => {
        const updated = structuredClone(main);
        const links = updated[mainIndex].links;

        if (Array.isArray(links)) {
            links.splice(subIndex, 1);
            if (links.length === 0) {
                delete updated[mainIndex].links;
            }
        }

        setMain(updated);
    };

    return (
        <>
            <Button onClick={() => setOpened(true)} size="xs" color='black'>
                Modifier
            </Button>

            <Modal opened={opened} onClose={() => setOpened(false)} title="Modifier le menu" size="xl" zIndex={2000}>
                <Accordion variant="contained" defaultValue="update">
                    <Accordion.Item value="update">
                        <Accordion.Control>🛠 Modifier le menu existant</Accordion.Control>
                        <Accordion.Panel>
                            <Stack>
                                <Title order={4}>Logo du site</Title>
                                <Dropzone imageUrl={logoUrl} setImageUrl={setLogoUrl} />

                                <Title order={4}>Meta Header</Title>
                                {meta.map((item, i) => (
                                    <Group key={`meta-${i}`} align="start">
                                        <TextInput label="Label" value={item.label} onChange={(e) => updateItem('meta', i, 'label', e.currentTarget.value)} />
                                        <TextInput label="Lien" value={item.link} onChange={(e) => updateItem('meta', i, 'link', e.currentTarget.value)} />
                                        <TextInput label="Icône" value={item.icon || ''} onChange={(e) => updateItem('meta', i, 'icon', e.currentTarget.value)} />
                                        <ActionIcon color="red" onClick={() => removeItem('meta', i)}><IconTrash /></ActionIcon>
                                    </Group>
                                ))}
                                <Button leftSection={<IconPlus size={16} />} onClick={() => addItem('meta')} variant="light">
                                    Ajouter un élément au metaHeader
                                </Button>

                                <Title order={4}>Menu principal</Title>
                                {main.map((item, i) => (
                                    <Stack key={`main-${i}`}>
                                        <Group align="start">
                                            <TextInput label="Label" value={item.label} onChange={(e) => updateItem('main', i, 'label', e.currentTarget.value)} />
                                            <Select
                                                label="Lien"
                                                placeholder="Choisir ou entrer"
                                                data={pageOptions}
                                                value={item.link}
                                                onChange={(val) => updateItem('main', i, 'link', val || '')}
                                                searchable
                                                clearable
                                                allowDeselect
                                            />
                                            <ActionIcon color="red" onClick={() => removeItem('main', i)}><IconTrash /></ActionIcon>
                                        </Group>
                                        {item.links?.map((sub, j) => (
                                            <Group key={`dropdown-${i}-${j}`} ml="lg">
                                                <TextInput label="Sous-label" value={sub.label} onChange={(e) => updateItem('main', i, 'label', e.currentTarget.value, j)} />
                                                <Select
                                                    label="Lien"
                                                    placeholder="Choisir ou entrer"
                                                    data={pageOptions}
                                                    value={sub.link}
                                                    onChange={(val) => updateItem('main', i, 'link', val || '', j)}
                                                    searchable
                                                    clearable
                                                    allowDeselect
                                                />
                                                <ActionIcon color="red" onClick={() => removeDropdownItem(i, j)}><IconTrash /></ActionIcon>
                                            </Group>
                                        ))}
                                        <Button size="xs" variant="outline" onClick={() => addDropdownItem(i)}>
                                            ➕ Ajouter un sous-lien
                                        </Button>
                                    </Stack>
                                ))}
                                <Button leftSection={<IconPlus size={16} />} onClick={() => addItem('main')} variant="light">
                                    Ajouter un élément au menu principal
                                </Button>

                                <Button fullWidth mt="md" onClick={handleSave}>
                                    💾 Enregistrer
                                </Button>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    {isSuperAdmin && <Accordion.Item value="create">
                        <Accordion.Control>✨ Créer une nouvelle page</Accordion.Control>
                        <Accordion.Panel>
                            <Stack>
                                <TextInput label="Slug de la page" placeholder="ex: notre-equipe" value={slug} onChange={(e) => setSlug(e.currentTarget.value)} />
                                <Textarea
                                    label="Description"
                                    placeholder="Décrivez le contenu souhaité pour cette page"
                                    value={description}
                                    onChange={(e) => setDescription(e.currentTarget.value)}
                                    autosize
                                    minRows={3}
                                />
                                <Switch label="Ajouter dans un menu déroulant ?" checked={isDropdown} onChange={(e) => setIsDropdown(e.currentTarget.checked)} />
                                {isDropdown && (
                                    <>
                                        <Select
                                            label="Menu déroulant cible"
                                            placeholder="Sélectionner un menu"
                                            data={availableDropdowns}
                                            value={selectedMenuLink}
                                            onChange={setSelectedMenuLink}
                                            disabled={createDropdown}
                                        />
                                        <Switch
                                            label="Créer le menu déroulant s’il n’existe pas encore"
                                            checked={createDropdown}
                                            onChange={(e) => setCreateDropdown(e.currentTarget.checked)}
                                        />
                                        {createDropdown && (
                                            <TextInput
                                                label="Nom du nouveau menu déroulant"
                                                placeholder="ex: Nos Métiers"
                                                value={newDropdownLabel}
                                                onChange={(e) => setNewDropdownLabel(e.currentTarget.value)}
                                                required
                                            />
                                        )}
                                    </>
                                )}
                                <Button loading={loading} fullWidth variant="default" onClick={handleCreatePage}>
                                    Générer la page ✨
                                </Button>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>}
                </Accordion>
            </Modal>
        </>
    );
}
